import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Trophy, Target, Clock, CheckCircle2, XCircle, 
  ChevronRight, BarChart3, BookOpen, Star, Eye,
  TrendingUp, Award, Zap
} from "lucide-react";

// All available simulations and mock tests
const ALL_SIMULATIONS = [
  ...Array.from({length: 10}, (_, i) => ({ id: `sim_easy_${i+1}`, name: `Easy ${i+1}`, difficulty: "easy" })),
  ...Array.from({length: 10}, (_, i) => ({ id: `sim_medium_${i+1}`, name: `Medium ${i+1}`, difficulty: "medium" })),
  ...Array.from({length: 10}, (_, i) => ({ id: `sim_hard_${i+1}`, name: `Hard ${i+1}`, difficulty: "hard" })),
];

const ALL_MOCK_TESTS = [
  { id: "official_mock_1", name: "Mock Test 1" },
  { id: "official_mock_2", name: "Mock Test 2" },
  { id: "official_mock_3", name: "Mock Test 3" },
];

const MyProgress = ({ auth }) => {
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      const response = await axios.get(`${API}/progress/my-tests`, { headers });
      setTestResults(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch progress:", error);
      toast.error("Failed to load progress");
    } finally {
      setLoading(false);
    }
  };

  // Get best result for each simulation/mock test
  const getBestResult = (testId) => {
    const results = testResults.filter(r => 
      r.simulation_id === testId || r.mock_test_id === testId || r.test_id === testId
    );
    if (results.length === 0) return null;
    // Return the result with highest score, or first result if all scores are 0
    return results.reduce((best, curr) => {
      if (!best) return curr;
      return curr.score > best.score ? curr : best;
    }, null);
  };

  // Get all attempts for a specific test
  const getAttempts = (testId) => {
    return testResults.filter(r => 
      r.simulation_id === testId || r.mock_test_id === testId || r.test_id === testId
    ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Calculate stats
  const completedSimulations = ALL_SIMULATIONS.filter(s => getBestResult(s.id)).length;
  const completedMockTests = ALL_MOCK_TESTS.filter(m => getBestResult(m.id)).length;
  const passedSimulations = ALL_SIMULATIONS.filter(s => getBestResult(s.id)?.passed).length;
  const passedMockTests = ALL_MOCK_TESTS.filter(m => getBestResult(m.id)?.passed).length;
  
  const totalAttempts = testResults.length;
  const avgScore = totalAttempts > 0 
    ? Math.round(testResults.reduce((sum, r) => sum + r.score, 0) / totalAttempts) 
    : 0;

  const difficultyColors = {
    easy: "bg-green-100 text-green-700",
    medium: "bg-yellow-100 text-yellow-700",
    hard: "bg-red-100 text-red-700"
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Progress Summary</h1>
          <p className="text-slate-600">Track your simulation and mock test performance</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{completedSimulations}/30</p>
                  <p className="text-xs text-blue-600">Simulations Done</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{completedMockTests}/3</p>
                  <p className="text-xs text-purple-600">Mock Tests Done</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{passedSimulations + passedMockTests}</p>
                  <p className="text-xs text-green-600">Tests Passed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{avgScore}%</p>
                  <p className="text-xs text-amber-600">Average Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Overview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-maple" />
              Overall Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Simulations ({completedSimulations}/30)</span>
                  <span>{Math.round((completedSimulations/30)*100)}%</span>
                </div>
                <Progress value={(completedSimulations/30)*100} className="h-3" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Mock Tests ({completedMockTests}/3)</span>
                  <span>{Math.round((completedMockTests/3)*100)}%</span>
                </div>
                <Progress value={(completedMockTests/3)*100} className="h-3" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Simulations and Mock Tests */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">All Tests</TabsTrigger>
            <TabsTrigger value="easy">Easy (10)</TabsTrigger>
            <TabsTrigger value="medium">Medium (10)</TabsTrigger>
            <TabsTrigger value="hard">Hard (10)</TabsTrigger>
            <TabsTrigger value="mock">Mock Tests (3)</TabsTrigger>
          </TabsList>

          {/* All Tests Overview */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...ALL_SIMULATIONS, ...ALL_MOCK_TESTS].map((test) => {
                const result = getBestResult(test.id);
                const attempts = getAttempts(test.id);
                const isMock = test.id.startsWith("mock_");
                
                return (
                  <Card 
                    key={test.id}
                    className={`border transition-all ${result ? (result.passed ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30') : 'border-slate-200'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {isMock ? (
                            <BookOpen className="w-5 h-5 text-purple-500" />
                          ) : (
                            <Zap className={`w-5 h-5 ${test.difficulty === 'easy' ? 'text-green-500' : test.difficulty === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} />
                          )}
                          <span className="font-medium">{test.name}</span>
                        </div>
                        {!isMock && (
                          <Badge className={difficultyColors[test.difficulty]}>
                            {test.difficulty}
                          </Badge>
                        )}
                      </div>
                      
                      {result ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {result.passed ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500" />
                              )}
                              <span className={`font-bold text-lg ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                                {result.score}%
                              </span>
                            </div>
                            <span className="text-xs text-slate-500">
                              {attempts.length} attempt{attempts.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">
                            {result.correct}/{result.total_questions} correct
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full mt-2"
                            onClick={() => setSelectedResult({ test, result, attempts })}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View Details
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-slate-400 text-sm">Not attempted</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Easy Simulations */}
          <TabsContent value="easy" className="mt-4">
            <SimulationGrid 
              simulations={ALL_SIMULATIONS.filter(s => s.difficulty === "easy")}
              getBestResult={getBestResult}
              getAttempts={getAttempts}
              difficultyColors={difficultyColors}
              onViewDetails={(test, result, attempts) => setSelectedResult({ test, result, attempts })}
            />
          </TabsContent>

          {/* Medium Simulations */}
          <TabsContent value="medium" className="mt-4">
            <SimulationGrid 
              simulations={ALL_SIMULATIONS.filter(s => s.difficulty === "medium")}
              getBestResult={getBestResult}
              getAttempts={getAttempts}
              difficultyColors={difficultyColors}
              onViewDetails={(test, result, attempts) => setSelectedResult({ test, result, attempts })}
            />
          </TabsContent>

          {/* Hard Simulations */}
          <TabsContent value="hard" className="mt-4">
            <SimulationGrid 
              simulations={ALL_SIMULATIONS.filter(s => s.difficulty === "hard")}
              getBestResult={getBestResult}
              getAttempts={getAttempts}
              difficultyColors={difficultyColors}
              onViewDetails={(test, result, attempts) => setSelectedResult({ test, result, attempts })}
            />
          </TabsContent>

          {/* Mock Tests */}
          <TabsContent value="mock" className="mt-4">
            <div className="grid md:grid-cols-3 gap-4">
              {ALL_MOCK_TESTS.map((test) => {
                const result = getBestResult(test.id);
                const attempts = getAttempts(test.id);
                
                return (
                  <Card 
                    key={test.id}
                    className={`border transition-all ${result ? (result.passed ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30') : 'border-slate-200'}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{test.name}</h3>
                          <p className="text-sm text-slate-500">20 questions • 45 min</p>
                        </div>
                      </div>
                      
                      {result ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {result.passed ? (
                                <Badge className="bg-green-100 text-green-700">Passed</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700">Failed</Badge>
                              )}
                            </div>
                            <span className={`font-bold text-2xl ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                              {result.score}%
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">
                            Best: {result.correct}/{result.total_questions} • {attempts.length} attempt{attempts.length !== 1 ? 's' : ''}
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full"
                            onClick={() => setSelectedResult({ test, result, attempts })}
                          >
                            <Eye className="w-4 h-4 mr-1" /> Review Answers
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-slate-400">Not attempted yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Result Details Dialog */}
        <Dialog open={selectedResult !== null} onOpenChange={() => setSelectedResult(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedResult && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {selectedResult.result?.passed ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                    {selectedResult.test.name} - {selectedResult.result?.score}%
                  </DialogTitle>
                  <DialogDescription>
                    {selectedResult.result?.correct}/{selectedResult.result?.total || 20} correct • 
                    {selectedResult.attempts?.length} total attempt{selectedResult.attempts?.length !== 1 ? 's' : ''}
                  </DialogDescription>
                </DialogHeader>

                {/* Attempt History */}
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Attempt History</h4>
                  <div className="flex gap-2 flex-wrap">
                    {selectedResult.attempts?.map((attempt, idx) => (
                      <Badge 
                        key={idx}
                        className={attempt.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                      >
                        #{selectedResult.attempts.length - idx}: {attempt.score}%
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Wrong Answers Review */}
                <div>
                  {(() => {
                    // Support both 'results' and 'answers' field names (backend inconsistency)
                    const answersData = selectedResult.result?.results?.length > 0 
                      ? selectedResult.result.results 
                      : (selectedResult.result?.answers || []);
                    const wrongAnswers = answersData.filter(a => !a.is_correct);
                    
                    return (
                      <>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          Questions to Review ({wrongAnswers.length})
                        </h4>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {wrongAnswers.map((answer, idx) => (
                            <Card key={idx} className="border-red-200 bg-red-50/50">
                              <CardContent className="p-4">
                                {answer.question_text ? (
                                  <p className="font-medium text-sm mb-2">Q{idx + 1}: {answer.question_text}</p>
                                ) : (
                                  <p className="font-medium text-sm mb-2 text-slate-500">Question {idx + 1}</p>
                                )}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="flex items-center gap-1">
                                    <XCircle className="w-4 h-4 text-red-500" />
                                    <span>Your answer: <strong>{answer.user_answer}</strong></span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                    <span>Correct: <strong>{answer.correct_answer}</strong></span>
                                  </div>
                                </div>
                                {answer.explanation && (
                                  <p className="text-xs text-slate-600 mt-2 bg-white p-2 rounded">
                                    💡 {answer.explanation}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                          {wrongAnswers.length === 0 && (
                            <p className="text-center text-green-600 py-4">
                              🎉 Perfect score! No wrong answers to review.
                            </p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Simulation Grid Component
const SimulationGrid = ({ simulations, getBestResult, getAttempts, difficultyColors, onViewDetails }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
    {simulations.map((test) => {
      const result = getBestResult(test.id);
      const attempts = getAttempts(test.id);
      
      return (
        <Card 
          key={test.id}
          className={`border cursor-pointer transition-all hover:shadow-md ${
            result 
              ? (result.passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50') 
              : 'border-slate-200 hover:border-slate-300'
          }`}
          onClick={() => result && onViewDetails(test, result, attempts)}
        >
          <CardContent className="p-3 text-center">
            <p className="font-medium text-sm">{test.name}</p>
            {result ? (
              <div className="mt-2">
                <span className={`text-lg font-bold ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {result.score}%
                </span>
                {result.passed && <Star className="w-4 h-4 text-yellow-500 inline ml-1" />}
                <p className="text-xs text-slate-500 mt-1">{attempts.length}x</p>
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-2">—</p>
            )}
          </CardContent>
        </Card>
      );
    })}
  </div>
);

export default MyProgress;
